/**
 * Proposed Disposition choices (`proposed_rejection_type`), one list for the
 * NCR create form and the NCR page.
 */
export interface DispositionOption {
  value: number;
  label: string;
}

const DISPOSITION_OPTIONS: DispositionOption[] = [
  { value: 0, label: 'Please Select' },
  { value: 1, label: 'Return to Supplier' },
  { value: 2, label: 'Scrap' },
  { value: 3, label: 'Rework' },
  { value: 4, label: 'Use As Is' },
  { value: 5, label: 'Sort' },
];
const RETURN_TO_SUPPLIER = 1;

/**
 * The choices to offer. E29: an internal NCR is never sent to the supplier, so
 * "Return to Supplier" is left out unless it is the NCR's saved choice.
 *
 * @param isInternal - the NCR is handled internally
 * @param current - the saved disposition, if any
 * @returns the options in display order
 */
export function dispositionOptionsFor(isInternal: boolean, current: number | null | undefined): DispositionOption[] {
  if (!isInternal || current === RETURN_TO_SUPPLIER) return DISPOSITION_OPTIONS;
  return DISPOSITION_OPTIONS.filter((o) => o.value !== RETURN_TO_SUPPLIER);
}

/** The create-form choices the disposition depends on. */
export interface DispositionContext {
  liability_type: number;
  is_internal: boolean;
  is_collection_required: boolean | null;
  is_credit_required: boolean | null;
  is_replacement_required: boolean | null;
}

/**
 * Whether the create form asks for a disposition: a claim process is
 * required, or the liability is the customer's. Mirrors the server's check
 * when the NCR is raised (NCRWorkflowService.submit_detail).
 *
 * @param form - the create form's liability choices
 * @returns true when Proposed Disposition is shown and required to raise
 */
export function dispositionRequired(form: DispositionContext): boolean {
  return form.liability_type === 2 || !!form.is_collection_required
    || !!form.is_credit_required || !!form.is_replacement_required;
}

/**
 * CR-8715: the create form's choices follow the selections above them.
 * A collection sends the parts back, so Return to Supplier is the only
 * disposition; otherwise a customer-liability NCR (internal or not) keeps
 * the parts, so Return to Supplier is left out.
 *
 * @param form - the create form's liability choices
 * @returns the options in display order, "Please Select" first
 */
export function createDispositionOptions(form: DispositionContext): DispositionOption[] {
  if (form.is_collection_required) {
    return DISPOSITION_OPTIONS.filter((o) => o.value === 0 || o.value === RETURN_TO_SUPPLIER);
  }
  if (form.liability_type === 2 || form.is_internal) {
    return DISPOSITION_OPTIONS.filter((o) => o.value !== RETURN_TO_SUPPLIER);
  }
  return DISPOSITION_OPTIONS;
}

/**
 * The disposition to keep after a choice above it changed: the only option
 * when there is just one, the current one while still offered, else none (0).
 *
 * @param form - the create form's liability choices
 * @param current - the disposition picked so far
 * @returns the disposition value to store
 */
export function fitDisposition(form: DispositionContext, current: number): number {
  const allowed = createDispositionOptions(form).filter((o) => o.value !== 0);
  if (allowed.length === 1) return allowed[0]!.value;
  return allowed.some((o) => o.value === current) ? current : 0;
}
